import {
  ONE_HOUR_OF_MS,
  getFullDayRangeTitle,
  ONE_DAY_OF_MS,
  getDayStart,
} from "../../../../../utils/timeUtils";
import type { ShiftSegment } from "../../../../../types/types";
import { FormatZonedFn } from "utils/customHooks/genericHooksTypes";

export const FULL_DAY_TITLE = `24 hr. view`;
export const NO_SHIFT_SCHEDULED = "Shift Not Specified";

export const getToday = (
  dayStartMsOffset: number,
  timeZoneMsOffset: number,
  formatZoned: FormatZonedFn
): ShiftSegment => {
  const ge = getTodayStart(dayStartMsOffset, timeZoneMsOffset);
  const lt = ge + ONE_DAY_OF_MS;
  return {
    shift: {
      name: getFullDayRangeTitle(ge, lt, formatZoned),
    },
    range: { ge, lt },
  };
};

// TECHDEBT: Remove the filter once askr doesn't send inverted segments
export const cleanShiftSegments = (
  shiftSegments: ShiftSegment[]
): ShiftSegment[] =>
  !shiftSegments
    ? []
    : shiftSegments
        .slice(2, -2)
        // map ranges to numeric
        .map(s => {
          return {
            ...s,
            range: { ...s.range, ge: +s.range.ge, lt: +s.range.lt },
          };
        })
        .filter(
          s =>
            +s.range.ge < +s.range.lt &&
            +s.range.lt - +s.range.ge < 26 * ONE_HOUR_OF_MS
        )
        .reduce((acc, s) => {
          if (acc.length === 0) {
            return [s];
          }
          const lasts = acc.slice(-1)[0];
          if (lasts.range.lt && +lasts.range.lt < +s.range.ge) {
            console.log("👷 Filling in a gap in the shifts!");
            return [
              ...acc,
              {
                shift: { name: NO_SHIFT_SCHEDULED, __typename: "Shift" },
                range: {
                  ge: +lasts.range.lt,
                  lt: +s.range.ge,
                  __typename: "TimeRangeFinite",
                },
                __typename: "ShiftSegment",
              } as ShiftSegment,
              s,
            ];
          }
          return [...acc, s];
        }, [] as ShiftSegment[]);

export const isShiftNameFullDay = shiftName =>
  shiftName && shiftName.includes(FULL_DAY_TITLE);

export const getShiftName = name => {
  const words = name.split(" ");
  const lowercaseWords = words.map(word => word.toLowerCase()).join(" ");
  const lastWord = words[words.length - 1];
  const anyLastWords =
    lowercaseWords.includes("shift") || // Don't add shift if already there
    lowercaseWords.includes("shifts...") || // Don't add shift if loading shifts
    lowercaseWords.includes("quart") || // Shift in french
    lowercaseWords.includes("not scheduled") // Don't add shift not scheduled
      ? ""
      : lastWord === "1"
        ? "st Shift"
        : lastWord === "2"
          ? "nd Shift"
          : lastWord === "3"
            ? "rd Shift"
            : lastWord && !isNaN(+lastWord)
              ? "th Shift"
              : ["", "changeover"].includes(name.toLowerCase())
                ? ""
                : " Shift";
  return `${name}${anyLastWords}`;
};

// Don't mess this up and make the range too small, otherwise
// it will continuously try to re-get shifts.

// shifts query has no maximum request length anymore,
// rendering gets expensive at around 300 elements on the page
// ~3 shifts per day
// so we don't want to render more than 100 days if we don't have to
// according to Matomo trackAnalyticsEvent (deprecated), average user clicks back 50 days on the calendar
// askr caches 7 days forward, anything beyond requires an extra call to midgard
// => fetch 70 days into the past, 5 days into the future
// * but ShiftSelectorContent renders one ShiftPickersCarousel per day, and this causes increased render times
// * when moving between days -> the only benefit to fetching more shifts is to preserve animation direction
// * => fetch 20 days into the past

export const BACKWARD_PADDING = 20 * ONE_DAY_OF_MS;
export const FORWARD_PADDING = 6 * ONE_DAY_OF_MS;

export const getShiftIndex = (shifts, t) =>
  shifts.findIndex(
    s => s.range.ge && s.range.lt && +s.range.ge <= +t && +s.range.lt > +t
  );

// Given a day and the shifts array,
// Get the 1st / 2nd / 3rd / etc shifts
// e.g. used in ShiftSelector to generate shift picker buttons,
// e.g. used in SegmentsProvider store to generate daysWithShifts
type getTodaysShiftsProps = {
  shifts: ShiftSegment[];
  selectedDay: ShiftSegment;
  dayStartMsOffset: number;
  timeZoneMsOffset: number;
  mustReturnAtLeastOne: boolean;
};
export const getTodaysShifts = ({
  shifts,
  selectedDay,
  dayStartMsOffset,
  timeZoneMsOffset,
  mustReturnAtLeastOne,
}: getTodaysShiftsProps): ShiftSegment[] =>
  // sometimes we want to find no shifts (e.g. avoid dupes to generate shifts list)
  // other times we want at least one (e.g. navigate to previous shift)
  {
    // if the current shift starts at 00:00, include it as today
    // (day starts at 00:00:00:00)
    const selectedDayGe = selectedDay?.range?.ge;

    const selectedDayStart =
      selectedDayGe && getDayStart(selectedDayGe, 0, 0) + dayStartMsOffset;

    if (selectedDayStart) {
      // add all shifts that are within today
      const newTodaysShifts = shifts.filter(
        shift =>
          // doesn't return BIG shifts that overlap days
          shift?.range?.ge &&
          // whose start is from midnight + dayStartMsOffset (inclusive)
          +shift.range.ge >= selectedDayStart &&
          // to next midnight + dayStartMsOffset (exclusive)
          +shift.range.ge < selectedDayStart + ONE_DAY_OF_MS
        // e.g. if day start time is 7am, the 7am shift is included in today.
      );

      if (newTodaysShifts.length === 0 && mustReturnAtLeastOne) {
        const bigShift = findLastBigShift(
          selectedDay,
          dayStartMsOffset,
          timeZoneMsOffset,
          shifts
        );
        if (bigShift) {
          newTodaysShifts.push(bigShift);
        }
      }

      return newTodaysShifts;
    }
    return [] as ShiftSegment[];
  };

export function getTodayStart(
  dayStartMsOffset: number,
  timeZoneMsOffset: number
): number {
  return getDayStart(
    Date.now() + timeZoneMsOffset,
    dayStartMsOffset,
    timeZoneMsOffset
  );
}

function findLastBigShift(
  newSelectedDay: ShiftSegment,
  dayStartMsOffset: number,
  timeZoneMsOffset: number,
  shifts: ShiftSegment[]
) {
  let searchPaddingDays = 0;
  let bigShift;
  // if no shifts today, search backward and forward for a BIG shift
  // search in progressively larger ranges
  // (to avoid finding yesterday/tomorrow's shift today)
  const geDt =
    newSelectedDay.range && newSelectedDay.range.ge
      ? +newSelectedDay.range.ge
      : Date.now();
  const selectedDayStart = getDayStart(
    geDt,
    dayStartMsOffset,
    timeZoneMsOffset
  );
  while (!bigShift && searchPaddingDays < 15) {
    bigShift = shifts.find(findShiftFn(selectedDayStart, searchPaddingDays));
    if (!bigShift) {
      searchPaddingDays += 0.25;
    }
  }
  return bigShift;
}
function findShiftFn(selectedDayStart: number, searchPaddingDays: number) {
  return shift =>
    shift &&
    shift.range &&
    shift.range.ge &&
    // whose start is from midnight + dayStartMsOffset (inclusive)
    +shift.range.ge >= +selectedDayStart - searchPaddingDays * ONE_DAY_OF_MS &&
    // to next midnight + dayStartMsOffset (exclusive)
    +shift.range.ge < +selectedDayStart + searchPaddingDays * ONE_DAY_OF_MS &&
    +shift.range.lt - +shift.range.ge >= ONE_DAY_OF_MS;
}
